import { useEffect, useState } from 'react';
import { HeaderMenu, HeaderMenuProvider } from './HeaderMenu';
import { NetworkMenu } from './NetworkMenu';
import { ProfileMenu } from './ProfileMenu';
import {
  Camera,
  ChevronDown,
  CircleHelp,
  Copy,
  Download,
  FilePlus2,
  FolderOpen,
  Grid3X3,
  ImageDown,
  Maximize2,
  PanelRightClose,
  PanelRightOpen,
  Redo2,
  Save,
  Sun,
  Undo2,
  View,
} from 'lucide-react';
import type { ExportFormat } from '../engine/exporters';
import type { StandardView } from './Viewport';
import { useStudioStore } from '../state/useStudioStore';

interface TopBarProps {
  onNotice: (message: string) => void;
  onAdChoices: () => void;
  onOpen: () => void;
  onSave: () => void;
  onExport: (format: ExportFormat) => void;
  onCapture: () => void;
  onFit: (selectionOnly?: boolean) => void;
  onView: (view: StandardView) => void;
  onProjection: () => void;
  onHelp: () => void;
  onNewBlank: () => void;
  onNewDemo: () => void;
}

function ProjectTitle(): JSX.Element {
  const document = useStudioStore((state) => state.document);
  const renameDocument = useStudioStore((state) => state.renameDocument);
  const [name, setName] = useState(document.name);
  useEffect(() => setName(document.name), [document.name]);

  const commit = () => {
    const next = name.trim() || 'Untitled model';
    setName(next);
    if (next !== document.name) renameDocument(next);
  };

  return (
    <input
      className="project-title"
      value={name}
      aria-label="Project name"
      onChange={(event) => setName(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur();
      }}
    />
  );
}

export function TopBar(props: TopBarProps): JSX.Element {
  const undo = useStudioStore((state) => state.undo);
  const redo = useStudioStore((state) => state.redo);
  const duplicateSelected = useStudioStore((state) => state.duplicateSelected);
  const deleteEntities = useStudioStore((state) => state.deleteEntities);
  const selectedIds = useStudioStore((state) => state.selectedIds);
  const pastCount = useStudioStore((state) => state.past.length);
  const futureCount = useStudioStore((state) => state.future.length);
  const settings = useStudioStore((state) => state.document.settings);
  const updateSettings = useStudioStore((state) => state.updateSettings);
  const sidebarOpen = useStudioStore((state) => state.sidebarOpen);
  const setSidebarOpen = useStudioStore((state) => state.setSidebarOpen);

  return (
    <HeaderMenuProvider>
    <header className="topbar">
      <div className="header-title-row">
      <div className="brand-lockup" aria-label="ConstructCanvas — Building Canvas">
        <span className="brand-mark" aria-hidden="true"><span /></span>
        <span className="brand-copy"><strong>ConstructCanvas</strong><small>Building Canvas</small></span>
      </div>

      <nav className="menu-strip" aria-label="Application menu">
        <HeaderMenu id="app-file" label={<>File <ChevronDown size={13} /></>}>
            <button type="button" onClick={props.onNewBlank}><FilePlus2 size={16} />New blank</button>
            <button type="button" onClick={props.onNewDemo}><View size={16} />Load demo</button>
            <span className="menu-separator" />
            <button type="button" onClick={props.onOpen}><FolderOpen size={16} />Open .ccmodel</button>
            <button type="button" onClick={props.onSave}><Save size={16} />Save .ccmodel <kbd>Ctrl S</kbd></button>
            <span className="menu-separator" />
            <button type="button" onClick={() => props.onExport('glb')}><Download size={16} />Export GLB</button>
            <button type="button" onClick={() => props.onExport('obj')}><Download size={16} />Export OBJ</button>
            <button type="button" onClick={() => props.onExport('stl')}><Download size={16} />Export STL</button>
            <button type="button" onClick={props.onCapture}><ImageDown size={16} />Export PNG</button>
        </HeaderMenu>

        <HeaderMenu id="app-edit" label={<>Edit <ChevronDown size={13} /></>}>
            <button type="button" disabled={!pastCount} onClick={undo}><Undo2 size={16} />Undo <kbd>Ctrl Z</kbd></button>
            <button type="button" disabled={!futureCount} onClick={redo}><Redo2 size={16} />Redo <kbd>Ctrl Y</kbd></button>
            <span className="menu-separator" />
            <button type="button" disabled={!selectedIds.length} onClick={duplicateSelected}><Copy size={16} />Duplicate <kbd>Ctrl D</kbd></button>
            <button type="button" disabled={!selectedIds.length} onClick={() => deleteEntities(selectedIds)}>Delete selection <kbd>Del</kbd></button>
        </HeaderMenu>

        <HeaderMenu id="app-view" label={<>View <ChevronDown size={13} /></>} popoverClassName="wide-menu">
            <button type="button" onClick={() => props.onFit(false)}><Maximize2 size={16} />Fit model</button>
            <button type="button" onClick={() => props.onFit(true)}>Fit selection</button>
            <button type="button" onClick={props.onProjection}>Toggle projection</button>
            <span className="menu-separator" />
            <div className="view-grid">
              {(['iso', 'top', 'front', 'back', 'left', 'right'] as StandardView[]).map((view) => (
                <button key={view} type="button" onClick={() => props.onView(view)}>{view}</button>
              ))}
            </div>
            <span className="menu-separator" />
            <button type="button" onClick={() => updateSettings({ showGrid: !settings.showGrid })}>
              <Grid3X3 size={16} />{settings.showGrid ? 'Hide' : 'Show'} grid
            </button>
            <button type="button" onClick={() => updateSettings({ shadows: !settings.shadows })}>
              <Sun size={16} />{settings.shadows ? 'Disable' : 'Enable'} shadows
            </button>
        </HeaderMenu>
      </nav>

      <NetworkMenu onNotice={props.onNotice} />
      </div>
      <div className="header-action-row">
      <div className="project-name-area"><ProjectTitle /></div>

      <div className="topbar-actions" role="group" aria-label="Project actions">
        <button type="button" className="icon-button" title="Undo (Ctrl/Cmd Z)" aria-label="Undo" disabled={!pastCount} onClick={undo}><Undo2 size={18} /></button>
        <button type="button" className="icon-button" title="Redo (Ctrl/Cmd Y)" aria-label="Redo" disabled={!futureCount} onClick={redo}><Redo2 size={18} /></button>
        <button type="button" className="primary-compact" onClick={props.onSave}><Save size={16} />Save</button>
        <button type="button" className="icon-button" title="Capture image" aria-label="Capture image" onClick={props.onCapture}><Camera size={18} /></button>
        <button type="button" className="icon-button" title="Help" aria-label="Help" onClick={props.onHelp}><CircleHelp size={18} /></button>
      </div>
      <div className="header-end-actions">
        <button
          type="button"
          className="icon-button"
          title={sidebarOpen ? 'Close inspector' : 'Open inspector'}
          aria-label={sidebarOpen ? 'Close inspector' : 'Open inspector'}
          aria-expanded={sidebarOpen} aria-controls={sidebarOpen ? 'model-inspector' : undefined}
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          {sidebarOpen ? <PanelRightClose size={18} /> : <PanelRightOpen size={18} />}
        </button>
        <ProfileMenu onSave={props.onSave} onHelp={props.onHelp} onAdChoices={props.onAdChoices} />
      </div>
      </div>
    </header>
    </HeaderMenuProvider>
  );
}
